import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import ResponsiveImage from '../common/ResponsiveImage'

const galleryImages = [
  {
    src: '/gallery/gallery-1.jpg',
    alt: 'Inside Recreo Rehab studio',
  },
  {
    src: '/gallery/gallery-2.jpg',
    alt: 'Recreo Rehab treatment space',
  },
  {
    src: '/gallery/gallery-3.jpg',
    alt: 'Recreo Rehab clinic interior',
  },
  {
    src: '/gallery/gallery-4.jpg',
    alt: 'Recreo Rehab therapy room',
  },
  {
    src: '/gallery/gallery-5.jpg',
    alt: 'Recreo Rehab studio detail',
  },
]

const ClinicCarousel = () => {
  const scrollRef = useRef(null)
  const doubled = [...galleryImages, ...galleryImages]

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    let raf
    let paused = false
    const speed = 0.5 // px per frame — gentle auto-scroll

    const tick = () => {
      if (!paused) {
        el.scrollLeft += speed
        const half = el.scrollWidth / 2
        if (el.scrollLeft >= half) el.scrollLeft -= half
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    const pause = () => {
      paused = true
    }
    const resume = () => {
      paused = false
    }

    // Mouse drag-to-scroll (touch/trackpad use native scrolling)
    let isDragging = false
    let startX = 0
    let startScroll = 0

    const onPointerDown = (e) => {
      pause()
      if (e.pointerType === 'mouse') {
        isDragging = true
        startX = e.pageX
        startScroll = el.scrollLeft
        el.setPointerCapture(e.pointerId)
      }
    }
    const onPointerMove = (e) => {
      if (!isDragging) return
      el.scrollLeft = startScroll - (e.pageX - startX)
    }
    const onPointerUp = () => {
      isDragging = false
      resume()
    }

    el.addEventListener('pointerenter', pause)
    el.addEventListener('pointerleave', onPointerUp)
    el.addEventListener('pointerdown', onPointerDown)
    el.addEventListener('pointermove', onPointerMove)
    el.addEventListener('pointerup', onPointerUp)
    el.addEventListener('touchstart', pause, { passive: true })
    el.addEventListener('touchend', resume)

    return () => {
      cancelAnimationFrame(raf)
      el.removeEventListener('pointerenter', pause)
      el.removeEventListener('pointerleave', onPointerUp)
      el.removeEventListener('pointerdown', onPointerDown)
      el.removeEventListener('pointermove', onPointerMove)
      el.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('touchstart', pause)
      el.removeEventListener('touchend', resume)
    }
  }, [])

  return (
    <section className="bg-cream-200 py-20 md:py-28 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-12 md:mb-16">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <span className="font-script text-brand-orange-500 italic text-lg mb-2 block">
            Our Space
          </span>
          <h2 className="font-display text-dark-700 text-2xl md:text-3xl lg:text-4xl font-semibold">
            Inside the Studio
          </h2>
        </motion.div>
      </div>

      <div
        ref={scrollRef}
        className="overflow-x-auto touch-pan-x cursor-grab active:cursor-grabbing select-none [&::-webkit-scrollbar]:hidden"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex gap-6 md:gap-8 w-max px-4 sm:px-6 lg:px-8">
          {doubled.map((image, index) => (
            <div
              key={index}
              className="flex-shrink-0 w-[260px] sm:w-[300px] md:w-[360px] lg:w-[420px]"
            >
              <div className="rounded-[20px] overflow-hidden aspect-[3/4]">
                <ResponsiveImage
                  src={image.src}
                  alt={image.alt}
                  sizes="(min-width: 1024px) 420px, (min-width: 768px) 360px, 300px"
                  className="w-full h-full object-cover pointer-events-none"
                  draggable="false"
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default ClinicCarousel
